import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Popover } from '@mui/material';
import { produce } from 'immer';
import icons from '@bbkAdminUtils/icons';
import { CollapsableForm } from '@bbkAdminComponents/play/play-utils';
import OfferPresentationMode, {
  OfferPresentationModeDropdown,
} from '@bbkAdminComponents/play/offer-presentation-mode';
import {
  ADD_OFFER_VALUE,
  PlayContext,
  PlayObjectProperty,
  PlayValidationErrorType,
  useDisableDemoReports,
  useShowPlayDemoData,
} from '@bbkAdminComponents/play/play-context';
import {
  type RulesNodeType,
  type RulesToTargetType,
  type TargetType,
} from '@bbkAdminUtils/api-client/routing.types';
import GenericSelect, {
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { offersAndLossAversionsSlice } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { generateNewIdInt } from '@bbkAdminComponents/experiences/offers/utils';
import { useSelectAllRules } from '@bbkAdminRedux/rtkq/rules.slice';
import { appPersistSlice } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { OfferNodeType } from '@bbkAdminComponents/play/play-offer-node-select';
import {
  RoutingNodeEnum,
  RoutingRulesEnum,
} from '@bbkAdminUtils/api-client/rest-routing-api';
import { useUrl } from '@bbkAdminUtils/nav';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useAppLocation, useHistory } from '@bbkShared/react-router';
import { AddControlButton } from '@bbkAdminComponents/play/add-control-button';
import { PlayControlSelect } from '@bbkAdminComponents/play/play-control-select';
import { DisableContainer } from '@bbkAdminComponents/play/disable-container';
import { GenericActionMenu } from '@bbkAdminComponents/action-menu/generic-action-menu';
import { ActionMenuIcons } from '@bbkAdminComponents/action-menu/action-menu-icons';
import { AIIcons } from '@bbkAdminComponents/experiences/offers/ai-icons';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { ManageOfferWarning } from '@bbkAdminComponents/play/offer-components/single-offer';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { useEnhancedOfferSelect } from '@bbkAdminComponents/play/offer-components/enhanced-offer-select';
import { useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { FeatureFlagsE } from '@bbkAdminUtils/feature-flags';
import { CategorizedOffer } from '@bbkAdminUtils/offer-categorization';

enum SelectProperty {
  RULE_ID = 'rule_id',
  OFFER = 'offer',
}

export type SubAudOfferErrorType = {
  type: OfferNodeType.SUB_AUDI_SPLIT;
  offerErrors: Array<string | undefined>;
  subAudErrors: Array<string | undefined>;
  hasError: boolean;
  hasValidOffer: boolean;
  show: boolean;
};

export const SubAudOffer = () => {
  const createUrl = useUrl();
  const history = useHistory();
  const {
    play,
    getPlayObjectProperty,
    setPlayObjectProperty,
    resetOfferNode,
    handleCreateNewOffer,
    offerOptions,
    playValidationErrors,
    setPlayValidationErrors,
    getDropdownTitle,
    getActiveTarget,
    handleEditOffer,
    handleCrossObjectiveOfferSelect,
    allOffers,
    genericSelectPredictiveAudiencesOpts,
    showManageOfferWarning,
  } = useContext(PlayContext);
  const offerPresentation = getPlayObjectProperty(
    PlayObjectProperty.PRESENTATION
  );
  const playType = getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE);
  const offerNode = getPlayObjectProperty(
    PlayObjectProperty.OFFER_NODE
  ) as RulesNodeType;
  const appKey = useSelector(selectAppId);
  const anchorRef = useRef(null);
  const [disableDemoReports] = useDisableDemoReports();
  const isDemoData = useShowPlayDemoData({ disableDemoReports });
  const rules = useSelectAllRules({
    appKey,
    active: true,
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  });
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const isMultiBranded = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );

  const appLocation = useAppLocation();
  const NEW_SUB_AUDIENCE = 'NEW_SUB_AUDIENCE';

  const showAddControl =
    getPlayObjectProperty(PlayObjectProperty.CONTROL_PERCENTAGE) === 0 &&
    getPlayObjectProperty(PlayObjectProperty.CONTROL_TARGET) === 'none';

  const { isLoading: isOffersLoading, isFetching: isOfferFetching } =
    offersAndLossAversionsSlice.endpoints.getOffers.useQuery({
      appKey,
      context: BBKRequestContext.lifecycle_reports,
    });

  const activeTarget = getActiveTarget();

  const offerData: {
    rule_id: string;
    target: { type: string; target_key?: string };
  }[] = useMemo(() => {
    if (!_.get(offerNode, 'routing_node.routing_rules')) {
      // init targeting node
      const initialNode = {
        ...offerNode,
        routing_node: {
          type: RoutingNodeEnum.rulesRoutingNode,
          name: generateNewIdInt('rule_split'),
          routing_rules: [
            {
              type: RoutingRulesEnum.routingToTargetRule,
              name: generateNewIdInt('rule_target'),
              targets: [
                {
                  type: activeTarget,
                },
              ],
            },
            {
              type: RoutingRulesEnum.routingToTargetRule,
              name: generateNewIdInt('rule_target'),
              targets: [
                {
                  type: activeTarget,
                },
              ],
            },
          ],
        },
      };
      setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, initialNode);
      return [];
    }
    const routingRules = _.get(
      offerNode,
      'routing_node.routing_rules',
      []
    ) as RulesToTargetType[];
    return routingRules.map((node) => ({
      rule_id: node.rule_id,
      target: node.targets[0] as TargetType,
    }));
  }, [offerNode]);

  const rulesSelectOptions: SelectOption<string>[] = useMemo(() => {
    const rulesOptions = rules.map(
      (rule: { display_name: string; id: string; is_ai: boolean }) => ({
        label: rule.display_name,
        value: rule.id,
        icon: rule.is_ai ? AIIcons.predictiveAiTag : <></>,
        is_ai: rule.is_ai,
      })
    );
    const addNewRuleOption = {
      label: (
        <span className="tw-font-semibold tw-text-cbrprimary-500">
          + New Sub-audience
        </span>
      ),
      value: NEW_SUB_AUDIENCE,
    };
    return [addNewRuleOption, ...rulesOptions];
  }, [rules]);

  const handlePropertyChange = (
    field: SelectProperty,
    value: string,
    index: number
  ) => {
    switch (field) {
      case SelectProperty.RULE_ID: {
        const updatedNode = produce(offerNode, (draft) => {
          _.set(
            draft,
            ['routing_node', 'routing_rules', index, 'rule_id'],
            value
          );
        });
        setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
        break;
      }
      case SelectProperty.OFFER: {
        const updatedNode = produce(offerNode, (draft) => {
          _.set(
            draft,
            [
              'routing_node',
              'routing_rules',
              index,
              'targets',
              '0',
              'target_key',
            ],
            value
          );
        });
        setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
        break;
      }
    }
  };

  const handleAddRow = () => {
    const node = {
      type: RoutingRulesEnum.routingToTargetRule,
      name: generateNewIdInt('rule_target'),
      targets: [
        {
          type: activeTarget,
        },
      ],
    };
    const updatedNode = produce(offerNode, (draft) => {
      _.set(draft, ['routing_node', 'routing_rules', offerData.length], node);
    });
    setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
  };

  const handleRemoveRow = (index: number) => {
    const updatedNode = produce(offerNode, (draft) => {
      const routingRules = _.get(draft, 'routing_node.routing_rules', []);
      routingRules.splice(index, 1);
      _.set(draft, ['routing_node', 'routing_rules'], routingRules);
    });
    setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
  };

  const enhancedOfferSelect = useEnhancedOfferSelect({
    offers: allOffers,
    playType: playType as any,
    offerPresentation,
    isMultiBranded,
    onOfferSelect: (offerId: string) => {
      // This will be handled by the individual offer selects in the rows
    },
    onCreateNewOffer: () => {
      setAddNewOfferPath(0);
      handleCreateNewOffer();
    },
    onCrossObjectiveOfferSelect: handleCrossObjectiveOfferSelect,
  });

  const setAddNewOfferPath = (index: number) => {
    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play,
          addNewOffer: {
            path: [
              'draft_targeting',
              'routing_percents',
              1,
              'routing_node',
              'routing_rules',
              index,
              'targets',
              0,
            ],
            value: undefined,
          },
        },
      })
    );
  };

  const navigateToNewSubAudiencePage = (index: number) => {
    const url = createUrl(AuthorizedPaths.audiencesNew);
    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play,
          subAudiencePath: [
            'draft_targeting',
            'routing_percents',
            1,
            'routing_node',
            'routing_rules',
            index,
            'rule_id',
          ],
        },
      })
    );
    history.push({
      pathname: url,
      state: {
        fromPlay: true,
      },
      search: encodeObjectForQuery({ returnUrl: appLocation.appUrl }),
    });
  };

  const validationErrorPath = [PlayValidationErrorType.Offer];

  const SubAudOfferValidationError = useMemo(() => {
    const errorNode = _.get(playValidationErrors, validationErrorPath);
    if (errorNode?.type === OfferNodeType.SUB_AUDI_SPLIT) {
      return errorNode;
    }
    return undefined;
  }, [playValidationErrors]);

  const showError = SubAudOfferValidationError?.show;

  useEffect(() => {
    const errors: (string | undefined)[] = [];
    const subAudErrors: (string | undefined)[] = [];
    let hasError = false;
    let hasValidOffer = false;
    offerData.forEach((data, index) => {
      const offer_key = _.get(data, ['target', 'target_key']);
      if (_.isEmpty(offer_key)) {
        errors[index] = 'Select a valid offer';
        hasError = true;
      } else {
        errors[index] = undefined;
        hasValidOffer = true;
      }

      if (_.isEmpty(data.rule_id)) {
        subAudErrors[index] = 'Select a valid sub-audience';
        hasError = true;
      } else {
        subAudErrors[index] = undefined;
      }
    });
    setPlayValidationErrors((prev) => {
      const updatedValidationErrors = produce(prev, (draft) => {
        _.set(draft, validationErrorPath, {
          type: OfferNodeType.SUB_AUDI_SPLIT,
          offerErrors: errors,
          subAudErrors,
          hasError,
          hasValidOffer,
          show: false,
        });
      });
      return updatedValidationErrors;
    });
  }, [offerData]);

  const title = (
    <div>
      <span className="tw-mr-2">Sub-audience split</span>
      <button ref={anchorRef} onClick={() => setOpenDropdown(true)}>
        {icons.threeDots}
      </button>
      <Popover
        open={openDropdown}
        anchorEl={anchorRef.current}
        onClose={() => setOpenDropdown(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div>
          <button
            className="tw-block tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-cbrdanger-400 hover:tw-bg-[#E1F1F9]"
            onClick={resetOfferNode}
          >
            <span className="tw-mr-1">{icons.trash1}</span>
            Remove
          </button>
        </div>
      </Popover>
    </div>
  );
  const getActions = (offerId: string | undefined, index: number) => [
    {
      icon: ActionMenuIcons.pencil,
      copy: 'Edit',
      onClick: () => {
        handleEditOffer(offerId);
        setAddNewOfferPath(index);
      },
    },
    {
      icon: ActionMenuIcons.duplicate,
      copy: 'Copy',
      onClick: () => {
        handleEditOffer(offerId, true);
        setAddNewOfferPath(index);
      },
    },
  ];

  return (
    <div className="ab-test-offer tw-flex tw-border tw-border-transparent">
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-mb-8 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#FABA7F] tw-bg-[#FEDBBB]">
          {icons.percent}
        </div>
      </div>
      <div className="tw-py-2 tw-pl-5">
        <DisableContainer editableAfterLive>
          <CollapsableForm
            title={title}
            id={3}
            collapseElement={<div className="tw-min-w-[45rem]" />}
          >
            {offerPresentation === undefined ? (
              <OfferPresentationMode />
            ) : (
              <div className="tw-mb-5 tw-mt-4 tw-min-w-[45rem] tw-rounded-sm tw-border tw-border-[#FABA7F] tw-bg-white tw-px-6 tw-py-3">
                <div className="tw-px-3 tw-py-2">
                  <div className="tw-flex tw-items-center">
                    <span className="tw-mr-2 tw-font-semibold">Widget:</span>
                    <OfferPresentationModeDropdown />
                    {showAddControl && <AddControlButton />}
                  </div>
                  {showAddControl && (
                    <div className="tw-my-5 tw-w-full tw-border-b tw-border-solid tw-border-[#DDDFE2] tw-px-10" />
                  )}
                  {!showAddControl && (
                    <PlayControlSelect
                      componentTitle="Sub-audience"
                      multipleOfferPages
                    />
                  )}
                  <div className="tw-mb-4 tw-pr-2">
                    <div className="tw-flex tw-font-semibold">
                      <div className="tw-flex-1 tw-pr-2">Sub-audiences</div>
                      <div className="tw-flex-1">
                        {getDropdownTitle()}
                        <span className="tw-w-5">
                          {(isOffersLoading || isOfferFetching) &&
                            icons.loaderSW6}
                        </span>
                      </div>
                      <div className="tw-w-16" />
                    </div>
                    {offerData?.map((row, index) => {
                      const offerId = row.target?.target_key;
                      const manageOfferWarning =
                        showManageOfferWarning(offerId);
                      return (
                        <div className="tw-mt-3 tw-flex" key={index}>
                          <div className="tw-flex-1 tw-items-center tw-pr-2">
                            <GenericSelect
                              options={rulesSelectOptions}
                              value={rulesSelectOptions.find(
                                (r) => r.value === row.rule_id
                              )}
                              onChange={(e: any) => {
                                if (e.value === NEW_SUB_AUDIENCE) {
                                  navigateToNewSubAudiencePage(index);
                                  return;
                                }
                                handlePropertyChange(
                                  SelectProperty.RULE_ID,
                                  e.value,
                                  index
                                );
                              }}
                              {...genericSelectPredictiveAudiencesOpts}
                            />
                            {showError &&
                              SubAudOfferValidationError?.subAudErrors[
                                index
                              ] && (
                                <span className="tw-pl-1 tw-text-xs tw-text-danger-500">
                                  {
                                    SubAudOfferValidationError?.subAudErrors[
                                      index
                                    ]
                                  }
                                </span>
                              )}
                          </div>
                          <div className="tw-flex-1">
                            <div className="tw-flex tw-flex-row tw-items-center">
                              <GenericSelect
                                className="tw-mr-2 tw-w-[90%]"
                                options={
                                  enhancedOfferSelect?.groupedOptions ||
                                  offerOptions
                                }
                                value={
                                  offerOptions.find(
                                    (o) => o.value === row.target?.target_key
                                  ) ?? null
                                }
                                onChange={(e) => {
                                  if (enhancedOfferSelect?.onSelect) {
                                    const crossObjectiveOffer =
                                      enhancedOfferSelect.categorization?.nonMatchingOffers.find(
                                        (categorizedOffer: CategorizedOffer) =>
                                          categorizedOffer.offer.id === e.value
                                      );
                                    if (crossObjectiveOffer) {
                                      setAddNewOfferPath(index);
                                      handleCrossObjectiveOfferSelect(
                                        crossObjectiveOffer.offer
                                      );
                                      return;
                                    }
                                    enhancedOfferSelect.onSelect(e.value);
                                  }

                                  if (e.value === ADD_OFFER_VALUE) {
                                    setAddNewOfferPath(index);
                                    handleCreateNewOffer();
                                    return;
                                  }
                                  handlePropertyChange(
                                    SelectProperty.OFFER,
                                    e.value,
                                    index
                                  );
                                }}
                              />
                              {offerOptions.find(
                                (o) => o.value === offerId
                              ) && (
                                <GenericActionMenu
                                  actions={getActions(offerId, index)}
                                />
                              )}
                              {manageOfferWarning && (
                                <TippyTooltip
                                  tippyOptions={{
                                    placement: 'right',
                                  }}
                                  content={<ManageOfferWarning />}
                                >
                                  <span className="tw-ml-4 tw-text-xs tw-text-[#FFB300] [&_svg]:tw-h-5 [&_svg]:tw-w-5">
                                    {icons.warning}
                                  </span>
                                </TippyTooltip>
                              )}
                            </div>
                            {showError &&
                              SubAudOfferValidationError?.offerErrors[
                                index
                              ] && (
                                <span className="tw-pl-1 tw-text-xs tw-text-danger-500">
                                  {
                                    SubAudOfferValidationError?.offerErrors[
                                      index
                                    ]
                                  }
                                </span>
                              )}
                          </div>

                          <div className="tw-w-4 tw-pl-1 tw-pt-1">
                            {index > 0 && (
                              <button
                                className="trash-icon tw-ml-1"
                                onClick={() => handleRemoveRow(index)}
                              >
                                <span>{icons.trash1}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    className="tw-mr-5 tw-font-semibold tw-text-[#295E7D]"
                    onClick={handleAddRow}
                  >
                    Add Sub-audience
                  </button>
                </div>
              </div>
            )}
          </CollapsableForm>
        </DisableContainer>
      </div>
    </div>
  );
};
